/**
 * Message Page Component
 * 
 * Simple page to display messages like success confirmations, errors, or notifications.
 * Uses the same theme as the rest of the application.
 */

import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import './MessagePage.css';

// Type definitions
interface MessagePageProps {
  title?: string;
  message?: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  showCloseButton?: boolean;
  autoClose?: number; // Auto close after X seconds
  redirectTo?: string;
}

/**
 * Message Page component
 */
const MessagePage: React.FC<MessagePageProps> = ({
  title,
  message,
  type = 'info',
  showCloseButton = true,
  autoClose,
  redirectTo,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get parameters from URL if not provided as props
  const displayTitle = title || searchParams.get('title') || 'Message';
  const displayMessage = message || searchParams.get('message') || 'Operation completed successfully.';
  const displayType = (type || searchParams.get('type') || 'info') as 'success' | 'error' | 'info' | 'warning';
  const displayShowCloseButton = showCloseButton !== false && searchParams.get('showClose') !== 'false';
  const displayAutoClose = autoClose || (searchParams.get('autoClose') ? parseInt(searchParams.get('autoClose')!) : undefined);
  const displayRedirectTo = redirectTo || searchParams.get('redirectTo') || '/';

  // Auto close functionality
  useEffect(() => {
    if (displayAutoClose && displayAutoClose > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, displayAutoClose * 1000);

      return () => clearTimeout(timer);
    }
  }, [displayAutoClose]);

  /**
   * Handle close action
   */
  const handleClose = () => {
    if (displayRedirectTo) {
      navigate(displayRedirectTo, { replace: true });
    } else {
      window.close();
    }
  };

  /**
   * Get icon based on message type
   */
  const getIcon = () => {
    switch (displayType) {
      case 'success':
        return (
          <svg className="message-page__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 12l2 2 4-4" />
            <circle cx="12" cy="12" r="10" />
          </svg>
        );
      case 'error':
        return (
          <svg className="message-page__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="message-page__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="message-page__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        );
    }
  };

  return (
    <div className="message-page">
      <div className="message-page__container">
        <div className={`message-page__content message-page__content--${displayType}`} data-tour="message-card">
          <div className="message-page__header">
            {getIcon()}
            <h1 className="message-page__title">{displayTitle}</h1>
          </div>
          
          <div className="message-page__body">
            <p className="message-page__message">{displayMessage}</p>
          </div>

          {displayShowCloseButton && (
            <div className="message-page__actions" data-tour="message-actions">
              <Button
                type="button"
                variant="primary"
                onClick={handleClose}
                className="message-page__close-button"
              >
                Close Page
              </Button>
            </div>
          )}

          {displayAutoClose && displayAutoClose > 0 && (
            <div className="message-page__footer">
              <p className="message-page__auto-close">
                This page will close automatically in {displayAutoClose} seconds.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagePage;
